/*
 * Teng search enhancer — crossref match card + job-intent notes.
 *
 * Calls the YerCommerce public search endpoint with the shopper's query.
 * Three additive behaviours on the search results page:
 *  1. CROSSREF: competitor part number recognised → match card with the Teng
 *     product + Add to Cart (POST /cart/add.js). Wording is trust-tiered:
 *     confirmed → "Confirmed cross-reference"; suggested → "Cross-reference —
 *     verify specs before ordering". NEVER the word "equivalent" (legal).
 *     Add to Cart is always a user click; a weak signal is logged after add.
 *  2. QUOTE CTA: part-number-ish query with no safe match → link to the RFQ
 *     quote tool (URL configured in the section settings).
 *  3. JOB-INTENT note / prompt (existing behaviour).
 *
 * Native Shopify results always stand alone — any API failure is silent.
 * All dynamic text via textContent (no XSS surface).
 */
(function () {
  var API = 'https://simplyfactura-api.fly.dev/api/commerce/storefront/search';
  var STOREFRONT = 'teng-usa'; // canonical slug from commerce.storefronts (verified live 2026-07-07)

  function qParam() {
    try {
      return (new URL(window.location.href).searchParams.get('q') || '').trim();
    } catch (_e) { return ''; }
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text; // textContent — no HTML injection
    return n;
  }

  function quoteUrl(host) { return host.getAttribute('data-quote-url') || ''; }

  function mount() {
    var q = qParam();
    if (!q || q.length < 4) return;
    var host = document.getElementById('teng-job-intent');
    if (!host) return;

    fetch(API + '?storefront=' + encodeURIComponent(STOREFRONT) + '&q=' + encodeURIComponent(q), {
      method: 'GET', headers: { Accept: 'application/json' },
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        if (data.crossref) host.appendChild(crossrefCard(data.crossref, q));
        else if (data.quoteCta) host.appendChild(quoteBanner(host));
        if (data.prompt) host.appendChild(banner('question', data.prompt, null));
        else if (data.note) host.appendChild(banner('note', data.note, data.matchedSituation));
        // When NATIVE search found nothing but the semantic layer did,
        // render its results — otherwise a good answer dies unseen
        // (e.g. "1997 Citroën BX engine socket" → spark plug sockets).
        if (data.results && data.results.length && nativeIsEmpty()) {
          host.appendChild(resultsStrip(data.results));
        }
      })
      .catch(function () { /* silent — native results stand alone */ });
  }

  // Native results are server-rendered before this script runs, so the DOM
  // is already settled. The theme's empty state prints "No results found".
  function nativeIsEmpty() {
    try {
      var main = document.querySelector('main') || document.body;
      return /No results found/i.test(main.textContent || '');
    } catch (_e) { return false; }
  }

  function resultsStrip(results) {
    var wrap = el('div', 'teng-closest');
    wrap.appendChild(el('div', 'teng-closest__title', 'Closest matches'));
    var row = el('div', 'teng-closest__row');
    results.slice(0, 4).forEach(function (r) {
      var card = document.createElement('a');
      card.className = 'teng-closest__card';
      card.href = '/products/' + r.handle;
      if (r.image) {
        var img = document.createElement('img');
        img.className = 'teng-closest__img';
        img.src = r.image; img.alt = r.title; img.loading = 'lazy';
        card.appendChild(img);
      }
      card.appendChild(el('div', 'teng-closest__name', r.title));
      if (r.price) card.appendChild(el('div', 'teng-closest__price', '$' + r.price));
      row.appendChild(card);
    });
    wrap.appendChild(row);
    return wrap;
  }

  function crossrefCard(x, rawQuery) {
    var card = el('div', 'teng-xref');
    if (x.image) {
      var img = document.createElement('img');
      img.className = 'teng-xref__img';
      img.src = x.image; img.alt = x.title; img.loading = 'lazy';
      card.appendChild(img);
    }
    var body = el('div', 'teng-xref__body');
    body.appendChild(el('div', 'teng-xref__tier',
      x.tier === 'exact' ? 'Exact part number match'
        : x.tier === 'confirmed' ? 'Confirmed cross-reference' : 'Cross-reference — verify specs before ordering'));
    var link = document.createElement('a');
    link.className = 'teng-xref__title';
    link.href = '/products/' + x.handle;
    link.textContent = x.title;
    body.appendChild(link);
    if (x.price) body.appendChild(el('div', 'teng-xref__price', '$' + x.price + (x.currency && x.currency !== 'USD' ? ' ' + x.currency : '')));
    card.appendChild(body);

    if (x.available && x.variantId) {
      var btn = el('button', 'teng-xref__add', 'Add to Cart');
      btn.type = 'button';
      btn.addEventListener('click', function () {
        btn.disabled = true; btn.textContent = 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(x.variantId), quantity: 1 }),
        }).then(function (r) {
          if (!r.ok) throw new Error('cart');
          btn.textContent = 'Added ✓';
          // weak confirmation signal — fire-and-forget, never promotes by itself
          fetch(API + '/xref-signal?storefront=' + encodeURIComponent(STOREFRONT), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ partNoNormalized: x.partNoNormalized, query: rawQuery, sku: x.sku, tier: x.tier }),
          }).catch(function () {});
          // let the theme refresh any cart-count bubble it renders
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }).catch(function () {
          btn.disabled = false; btn.textContent = 'Add to Cart';
        });
      });
      card.appendChild(btn);
    } else {
      card.appendChild(el('div', 'teng-xref__oos', 'Currently unavailable online'));
    }
    return card;
  }

  function quoteBanner(host) {
    var wrap = el('div', 'teng-jobintent teng-jobintent--question');
    // Product-voice: state the capability, never chat. (Copy devoice 2026-07-07.)
    wrap.appendChild(el('div', 'teng-jobintent__msg',
      'Competitor part number cross-referencing available — single items or full lists.'));
    var url = quoteUrl(host);
    if (url) {
      var a = document.createElement('a');
      a.className = 'teng-jobintent__report';
      a.href = url;
      a.textContent = 'Get a quote';
      wrap.appendChild(a);
    }
    return wrap;
  }

  function banner(kind, message, situation) {
    var wrap = el('div', 'teng-jobintent teng-jobintent--' + kind);
    wrap.appendChild(el('div', 'teng-jobintent__msg', message));
    if (situation) {
      var report = el('button', 'teng-jobintent__report', 'Report incorrect match');
      report.type = 'button';
      report.addEventListener('click', function () {
        fetch(API + '/report?storefront=' + encodeURIComponent(STOREFRONT), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ situation: situation }),
        }).then(function () {
          wrap.textContent = '';
          wrap.appendChild(el('div', 'teng-jobintent__msg', 'Reported. This match has been removed for review.'));
        }).catch(function () { report.textContent = 'Reported'; });
      });
      wrap.appendChild(report);
    }
    return wrap;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
  else mount();
})();
